<template>
    <div class="drivers-page">
        <!-- Header -->
        <section class="page-header">
            <div class="container">
                <nav class="breadcrumbs">
                    <router-link to="/">Inicio</router-link>
                    <span class="separator">/</span>
                    <span>Soporte</span>
                    <span class="separator">/</span>
                    <span class="current">Drivers y Descargas</span>
                </nav>
                <h1 class="page-title">Drivers y Descargas</h1>
                <p class="page-subtitle">
                    Encuentra y descarga los drivers y documentación de nuestros productos
                </p>
            </div>
        </section>
        
        <div class="container">
            <!-- Search -->
            <div class="search-section">
                <div class="search-box">
                    <i class="fas fa-search"></i>
                    <input 
                        type="text" 
                        v-model="searchQuery"
                        @input="filterProducts"
                        placeholder="Buscar por nombre, modelo o SKU..."
                    >
                </div>
            </div>
            
            <!-- Categories Tabs -->
            <div v-if="categories && categories.length > 0" class="categories-tabs">
                <button 
                    :class="['category-tab', { active: selectedCategory === null }]"
                    @click="selectCategory(null)"
                >
                    Todos
                </button>
                <button 
                    v-for="categoria in categories" 
                    :key="categoria.id"
                    :class="['category-tab', { active: selectedCategory === categoria.id }]"
                    @click="selectCategory(categoria.id)"
                >
                    {{ categoria.nombre }}
                </button>
            </div>
            
            <!-- Products by Category -->
            <div v-if="loading" class="loading-state">
                <div class="spinner"></div>
                <p>Cargando productos...</p>
            </div>
            
            <div v-else-if="filteredProducts.length === 0" class="empty-state">
                <i class="fas fa-folder-open"></i>
                <h3>No se encontraron productos</h3>
                <p>Intenta ajustar tu búsqueda o filtros</p>
            </div>
            
            <div v-else>
                <!-- Modo: Mostrar todos los productos sin agrupar -->
                <div class="products-grid">
                    <div 
                        v-for="product in displayedProducts" 
                        :key="product.id"
                        class="product-card"
                        @click="openModal(product)"
                    >
                        <div class="product-image">
                            <img :src="getProductImage(product)" :alt="product.nombre">
                        </div>
                        
                        <div class="product-info">
                            <h3 class="product-name">{{ product.nombre }}</h3>
                            <div class="product-meta">
                                <span v-if="product.marca" class="meta-badge marca">
                                    {{ product.marca.nombre }}
                                </span>
                                <span v-if="product.modelo" class="meta-badge modelo">
                                    {{ product.modelo.nombre }}
                                </span>
                            </div>
                            <p class="product-sku">SKU: {{ product.sku }}</p>
                        </div>
                        
                        <div class="product-overlay">
                            <i class="fas fa-search-plus"></i>
                            <span>Ver detalles</span>
                        </div>
                    </div>
                </div>
                
                <!-- Loading more indicator -->
                <div v-if="hasMoreProducts" class="loading-more">
                    <div class="spinner-small"></div>
                    <p>Cargando más productos...</p>
                </div>
                
                <!-- No more products indicator -->
                <div v-else-if="displayedProducts.length < filteredProducts.length" class="no-more-products">
                    <p>{{ displayedProducts.length }} de {{ filteredProducts.length }} productos</p>
                </div>
            </div>
        </div>
        
        <!-- Modal -->
        <transition name="modal">
            <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
                <div class="modal-content">
                    <button class="modal-close" @click="closeModal">
                        <i class="fas fa-times"></i>
                    </button>
                    
                    <div class="modal-body">
                        <div class="modal-image">
                            <img :src="getProductImage(selectedProduct)" :alt="selectedProduct.nombre">
                        </div>
                        
                        <div class="modal-info">
                            <h2 class="modal-title">{{ selectedProduct.nombre }}</h2>
                            
                            <div class="modal-meta">
                                <div v-if="selectedProduct.marca" class="meta-item">
                                    <label>Marca:</label>
                                    <span class="meta-value marca-value">{{ selectedProduct.marca.nombre }}</span>
                                </div>
                                <div v-if="selectedProduct.modelo" class="meta-item">
                                    <label>Modelo:</label>
                                    <span class="meta-value modelo-value">{{ selectedProduct.modelo.nombre }}</span>
                                </div>
                                <div v-if="selectedProduct.categoria" class="meta-item">
                                    <label>Categoría:</label>
                                    <span class="meta-value categoria-value">{{ selectedProduct.categoria.nombre }}</span>
                                </div>
                                <div class="meta-item">
                                    <label>SKU:</label>
                                    <span class="meta-value">{{ selectedProduct.sku }}</span>
                                </div>
                            </div>
                            
                            <div class="modal-actions">
                                <a 
                                    v-if="selectedProduct.brochure" 
                                    :href="`/storage/${selectedProduct.brochure}`" 
                                    target="_blank"
                                    class="modal-btn btn-brochure"
                                >
                                    <i class="fas fa-file-pdf"></i>
                                    <span>Ficha Técnica</span>
                                </a>
                                
                                <a 
                                    v-if="selectedProduct.link_driver" 
                                    :href="selectedProduct.link_driver" 
                                    target="_blank"
                                    class="modal-btn btn-driver"
                                >
                                    <i class="fas fa-download"></i>
                                    <span>Driver</span>
                                </a>
                                
                                <a 
                                    v-if="selectedProduct.link_manual" 
                                    :href="selectedProduct.link_manual" 
                                    target="_blank"
                                    class="modal-btn btn-manual"
                                >
                                    <i class="fas fa-book"></i>
                                    <span>Manual</span>
                                </a>
                                
                                <router-link 
                                    :to="{ name: 'producto.detalle', params: { id: selectedProduct.id, slug: selectedProduct.slug }}"
                                    class="modal-btn btn-details"
                                    @click.native="closeModal"
                                >
                                    <i class="fas fa-info-circle"></i>
                                    <span>Ver más detalles</span>
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import api from '../services/api';

export default {
    name: 'Drivers',
    data() {
        return {
            products: [],
            categories: [],
            filteredProducts: [],
            displayedProducts: [], // Productos que se muestran con scroll
            selectedCategory: null,
            searchQuery: '',
            loading: true,
            showModal: false,
            selectedProduct: {},
            firstLoad: true,
            productsPerPage: 12, // Cuántos productos mostrar por vez
            currentPage: 1
        };
    },
    async created() {
        await this.loadCategories();
        await this.loadProducts();
        // NO seleccionar categoría por defecto, iniciar con "Todos"
        this.selectedCategory = null;
        this.filterProducts();
        
        // Agregar listener de scroll para lazy loading
        window.addEventListener('scroll', this.handleScroll);
    },
    beforeDestroy() {
        // Limpiar listener de scroll
        window.removeEventListener('scroll', this.handleScroll);
    },
    computed: {
        hasMoreProducts() {
            return this.displayedProducts.length < this.filteredProducts.length;
        }
    },
    methods: {
        async loadCategories() {
            try {
                console.log('Cargando categorías...');
                const { data } = await api.getCategories();
                this.categories = data.filter(c => c.activo);
                console.log('Categorías cargadas:', this.categories.length, 'activas');
            } catch (error) {
                console.error('Error loading categories:', error);
                this.categories = [];
            }
        },
        async loadProducts() {
            try {
                this.loading = true;
                const { data } = await api.getProducts();
                this.products = data || [];
                console.log('Products loaded:', this.products.length, 'products');
            } catch (error) {
                console.error('Error loading products:', error);
                this.products = [];
                this.filteredProducts = [];
            } finally {
                this.loading = false;
            }
        },
        selectCategory(categoryId) {
            console.log('Categoría seleccionada:', categoryId === null ? 'Todos' : categoryId);
            this.selectedCategory = categoryId;
            this.filterProducts();
        },
        filterProducts() {
            let filtered = [...this.products];
            
            // Filter by category
            if (this.selectedCategory !== null) {
                filtered = filtered.filter(p => p.categoria_id === this.selectedCategory);
                console.log(`Productos filtrados por categoría ${this.selectedCategory}:`, filtered.length);
            } else {
                console.log('Mostrando todos los productos:', filtered.length);
            }
            
            // Filter by search
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = filtered.filter(p => 
                    p.nombre.toLowerCase().includes(query) ||
                    (p.sku && p.sku.toLowerCase().includes(query)) ||
                    (p.marca && p.marca.nombre.toLowerCase().includes(query)) ||
                    (p.modelo && p.modelo.nombre.toLowerCase().includes(query))
                );
            }
            
            this.filteredProducts = filtered;
            
            // Resetear paginación y cargar primeros productos
            this.currentPage = 1;
            this.loadMoreProducts(true);
        },
        loadMoreProducts(reset = false) {
            if (reset) {
                this.displayedProducts = [];
                this.currentPage = 1;
            }
            
            const startIndex = (this.currentPage - 1) * this.productsPerPage;
            const endIndex = startIndex + this.productsPerPage;
            const newProducts = this.filteredProducts.slice(startIndex, endIndex);
            
            if (reset) {
                this.displayedProducts = newProducts;
            } else {
                this.displayedProducts = [...this.displayedProducts, ...newProducts];
            }
            
            console.log(`Mostrando ${this.displayedProducts.length} de ${this.filteredProducts.length} productos`);
        },
        handleScroll() {
            // Verificar si el usuario está cerca del final de la página
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            
            // Si está a 300px del final y hay más productos
            if (scrollTop + windowHeight >= documentHeight - 300 && this.hasMoreProducts) {
                this.currentPage++;
                this.loadMoreProducts();
            }
        },
        getProductImage(product) {
            return product.imagen_principal 
                ? `/storage/${product.imagen_principal}` 
                : '/storage/no_image.jpg?text=Producto';
        },
        openModal(product) {
            this.selectedProduct = product;
            this.showModal = true;
            document.body.style.overflow = 'hidden';
        },
        closeModal() {
            this.showModal = false;
            document.body.style.overflow = '';
        }
    }
};
</script>

<style scoped>
.drivers-page {
    background-color: var(--background);
    min-height: 100vh;
    padding-top: 80px;
}

.page-header {
    background: linear-gradient(135deg, var(--primary-dark), var(--primary-blue));
    padding: 60px 2rem;
    position: relative;
}

.page-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 50%, rgba(212, 175, 55, 0.1), transparent 60%);
}

.breadcrumbs {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
}

.breadcrumbs a {
    color: var(--text-gray);
    text-decoration: none;
    transition: color 0.3s ease;
}

.breadcrumbs a:hover {
    color: var(--accent-gold);
}

.breadcrumbs .separator {
    color: rgba(212, 175, 55, 0.3);
}

.breadcrumbs .current {
    color: var(--accent-gold);
    font-weight: 600;
}

.container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 1;
}

.page-title {
    font-family: 'Playfair Display', serif;
    font-size: 3rem;
    margin-bottom: 1rem;
    color: var(--text-light);
}

.page-subtitle {
    font-size: 1.2rem;
    color: var(--text-gray);
}

.search-section {
    margin: 3rem 0;
}

.search-box {
    position: relative;
    max-width: 600px;
    margin: 0 auto;
}

.search-box i {
    position: absolute;
    left: 1.5rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--accent-gold);
    font-size: 1.25rem;
}

.search-box input {
    width: 100%;
    padding: 1.25rem 1.5rem 1.25rem 4rem;
    background: var(--card-bg);
    border: 2px solid rgba(212, 175, 55, 0.2);
    border-radius: 50px;
    color: var(--text-light);
    font-size: 1rem;
    transition: all 0.3s ease;
}

.search-box input::placeholder {
    color: var(--text-gray);
}

.search-box input:focus {
    outline: none;
    border-color: var(--accent-gold);
    background: rgba(255, 255, 255, 0.08);
}

.categories-tabs {
    display: flex;
    gap: 1rem;
    margin-bottom: 3rem;
    overflow-x: auto;
    padding-bottom: 0.5rem;
}

.category-tab {
    padding: 0.75rem 1.5rem;
    border: 2px solid rgba(212, 175, 55, 0.2);
    background: var(--card-bg);
    color: var(--text-gray);
    border-radius: 50px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.category-tab:hover {
    border-color: var(--accent-gold);
    color: var(--accent-gold);
}

.category-tab.active {
    background: linear-gradient(135deg, var(--accent-gold), var(--accent-teal));
    border-color: transparent;
    color: var(--primary-dark);
}

.products-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 2rem;
    margin-bottom: 3rem;
}

.product-card {
    background: var(--card-bg);
    border-radius: 16px;
    overflow: hidden;
    border: 1px solid rgba(212, 175, 55, 0.1);
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
}

.product-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(212, 175, 55, 0.2);
    border-color: var(--accent-gold);
}

.product-image {
    width: 100%;
    height: 250px;
    background: rgba(255, 255, 255, 0.05);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
}

.product-image img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.product-info {
    padding: 1.5rem;
}

.product-name {
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--text-light);
    margin-bottom: 1rem;
    min-height: 2.5rem;
}

.product-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
}

.meta-badge {
    padding: 0.25rem 0.75rem;
    border-radius: 50px;
    font-size: 0.75rem;
    font-weight: 600;
}

.meta-badge.marca {
    background: rgba(212, 175, 55, 0.2);
    color: var(--accent-gold);
}

.meta-badge.modelo {
    background: rgba(45, 212, 191, 0.2);
    color: var(--accent-teal);
}

.product-sku {
    color: var(--text-gray);
    font-size: 0.875rem;
}

.product-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(30, 58, 95, 0.95);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.product-card:hover .product-overlay {
    opacity: 1;
}

.product-overlay i {
    font-size: 2rem;
    color: var(--accent-gold);
}

.product-overlay span {
    color: var(--text-light);
    font-weight: 600;
}

/* MODAL */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 2rem;
}

.modal-content {
    background: var(--card-bg);
    border-radius: 20px;
    max-width: 900px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    border: 1px solid rgba(212, 175, 55, 0.2);
}

.modal-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(212, 175, 55, 0.1);
    border: none;
    color: var(--accent-gold);
    font-size: 1.25rem;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 10;
}

.modal-close:hover {
    background: var(--accent-gold);
    color: var(--primary-dark);
}

.modal-body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    padding: 3rem;
}

.modal-image {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 16px;
    padding: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.modal-image img {
    width: 100%;
    height: auto;
    object-fit: contain;
    max-height: 400px;
}

.modal-info {
    display: flex;
    flex-direction: column;
    gap: 2rem;
}

.modal-title {
    font-family: 'Playfair Display', serif;
    font-size: 2rem;
    color: var(--text-light);
    line-height: 1.3;
}

.modal-meta {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem;
    background: rgba(212, 175, 55, 0.05);
    border-left: 3px solid var(--accent-gold);
    border-radius: 10px;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.meta-item label {
    font-weight: 600;
    color: var(--text-gray);
    min-width: 80px;
}

.meta-value {
    color: var(--text-light);
    font-weight: 600;
}

.marca-value {
    color: var(--accent-gold);
}

.modelo-value {
    color: var(--accent-teal);
}

.categoria-value {
    color: var(--text-light);
}

.modal-actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.modal-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 1rem 2rem;
    border-radius: 50px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    border: 2px solid transparent;
}

.btn-brochure {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
    border-color: #ef4444;
}

.btn-brochure:hover {
    background: #ef4444;
    color: #fff;
}

.btn-driver {
    background: rgba(45, 212, 191, 0.1);
    color: var(--accent-teal);
    border-color: var(--accent-teal);
}

.btn-driver:hover {
    background: var(--accent-teal);
    color: var(--primary-dark);
}

.btn-manual {
    background: rgba(139, 92, 246, 0.1);
    color: #8b5cf6;
    border-color: #8b5cf6;
}

.btn-manual:hover {
    background: #8b5cf6;
    color: #fff;
}

.btn-details {
    background: rgba(212, 175, 55, 0.1);
    color: var(--accent-gold);
    border-color: var(--accent-gold);
}

.btn-details:hover {
    background: var(--accent-gold);
    color: var(--primary-dark);
}

/* TRANSITIONS */
.modal-enter-active, .modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
    transition: transform 0.3s ease;
}

.modal-enter, .modal-leave-to {
    opacity: 0;
}

.modal-enter .modal-content,
.modal-leave-to .modal-content {
    transform: scale(0.9);
}

/* LOADING & EMPTY STATES */
.loading-state,
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 5rem 2rem;
    text-align: center;
}

.spinner {
    width: 50px;
    height: 50px;
    border: 4px solid rgba(212, 175, 55, 0.1);
    border-top-color: var(--accent-gold);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.loading-state p {
    margin-top: 1rem;
    color: var(--text-gray);
}

.empty-state i {
    font-size: 4rem;
    color: rgba(212, 175, 55, 0.3);
    margin-bottom: 1rem;
}

.empty-state h3 {
    font-size: 1.5rem;
    color: var(--text-light);
    margin-bottom: 0.5rem;
}

.empty-state p {
    color: var(--text-gray);
}

/* LOADING MORE */
.loading-more,
.no-more-products {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    text-align: center;
}

.spinner-small {
    width: 30px;
    height: 30px;
    border: 3px solid rgba(212, 175, 55, 0.1);
    border-top-color: var(--accent-gold);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 0.5rem;
}

.loading-more p {
    color: var(--text-gray);
    font-size: 0.9rem;
}

.no-more-products p {
    color: var(--text-gray);
    font-size: 0.9rem;
    font-style: italic;
}

/* RESPONSIVE */
@media (max-width: 768px) {
    .page-title {
        font-size: 2rem;
    }
    
    .modal-body {
        grid-template-columns: 1fr;
        padding: 2rem;
    }
    
    .products-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 576px) {
    .container {
        padding: 0 1rem;
    }
    
    .modal-body {
        padding: 1.5rem;
    }
}
</style>